import React, { ComponentProps, ReactElement } from "react";
import { always, tw } from "src/utils";
import { CpBadgeProps } from "./cp-badge";
import { CpBadgeGroupVariants } from "./cp-badge-variants/cp-badge-group-variants";

type CpBadgeGroupProps = {
  direction?: "row" | "column";
} & ComponentProps<"div">;

type CpBadgeElement = ReactElement<CpBadgeProps>;

export const CpBadgeGroup = (props: CpBadgeGroupProps) => {
  const { children, className, direction = "row", ...rest } = props;

  // Convert children to array and filter out non-badge elements
  const badges = React.Children.toArray(children).filter(
    (child): child is CpBadgeElement => React.isValidElement(child),
  );

  if (badges.length === 0) {
    return null;
  }

  /**
   * Determine the position of a badge within the group
   */
  const getPosition = (index: number, total: number) => {
    if (total === 1) return "only";
    if (index === 0) return "first";
    if (index === total - 1) return "last";
    return "middle";
  };

  if (badges.length === 1) {
    // If only one badge, return it without modifications
    return (
      <div className={className} {...rest}>
        {badges[0]}
      </div>
    );
  }

  return (
    <div
      className={always(
        tw(
          "flex",
          direction === "column" ? "flex-col" : "flex-row",
          "w-fit", // Container fits content, badges control their own width
        ),
        className,
      )}
      {...rest}
    >
      {badges.map((badge, index) => {
        const position = getPosition(index, badges.length);
        return React.cloneElement(badge, {
          key: badge.key || index,
          className: always(
            badge.props.className || "",
            tw(CpBadgeGroupVariants({ direction, position })),
          ),
        });
      })}
    </div>
  );
};
